/**
 * Quote API Service
 * Handles quote fetching, acceptance, PO reference submission, and document access
 */
import * as Sentry from '@sentry/browser';
import { getAuthHeaders } from './common.js';

const API_BASE = import.meta.env.VITE_API_GATEWAY_URL;
const QUOTE_API_PREFIX = '/invoice/v1';

/**
 * Thrown when a document download returns HTTP 409 (not yet generated).
 * Contains the estimated wait time from the Retry-After header.
 */
export class DocumentNotReadyError extends Error {
    constructor(retryAfterMinutes) {
        super('Document not ready');
        this.name = 'DocumentNotReadyError';
        this.retryAfterMinutes = retryAfterMinutes;
    }
}

/**
 * Thrown when an API call returns HTTP 403 (contact not authorized).
 * The authenticated user is not a recognised contact for the quote.
 */
export class ContactNotAuthorizedError extends Error {
    constructor(quoteId, apiMessage) {
        super(`Contact not authorized for quote: ${quoteId}`);
        this.name = 'ContactNotAuthorizedError';
        this.quoteId = quoteId;
        this.apiMessage = apiMessage;
    }
}

/**
 * Handle a 403 response by logging, adding a Sentry breadcrumb, and throwing.
 * Used by mutation and download functions where the user is already viewing a quote.
 */
async function handle403(response, quoteId) {
    let body = null;
    try {
        body = await response.json();
    } catch (_) {
        /* ignore */
    }
    if (import.meta.env.DEV) {
        console.log(`[QuoteAPI] Not authorized for quote: ${quoteId}`, body);
    }
    Sentry.addBreadcrumb({
        category: 'quote',
        message: `Contact not authorized for quote: ${quoteId}`,
        level: 'warning',
        data: { apiMessage: body?.message || null },
    });
    throw new ContactNotAuthorizedError(quoteId, body?.message || null);
}

/**
 * Map API status (UPPERCASE) to internal app status (kebab-case)
 *
 * @param {string} apiStatus - Status from API (PENDING, SENT, ACCEPTED, PAID)
 * @param {string} [isMUF='Y'] - Money Upfront flag from API ("Y" or "N")
 * @returns {string} Internal status for component rendering
 */
export function mapApiStatus(apiStatus, isMUF = 'Y') {
    const isMoneyUpfront = isMUF === 'Y';

    switch (apiStatus) {
        case 'PENDING':
            return 'pending';
        case 'SENT':
            return 'pending-acceptance';
        case 'ACCEPTED':
            return isMoneyUpfront ? 'pending-payment' : 'confirmed-charge-account';
        case 'PAID':
            return 'confirmed';
        default:
            if (import.meta.env.DEV) {
                console.warn(`[QuoteAPI] Unknown API status: "${apiStatus}"`);
            }
            Sentry.captureMessage(`Unknown API quote status: "${apiStatus}"`, {
                level: 'warning',
                tags: { feature: 'quote-status-mapping' },
                extra: { apiStatus, isMUF },
            });
            return 'error';
    }
}

/**
 * Fetch quote data by reference
 *
 * @param {string} quoteId - Quote reference ID
 * @returns {Promise<Object|null>} Quote data object, or null if not found
 * @throws {Error} On server/network errors
 */
export async function fetchQuote(quoteId) {
    if (!API_BASE) {
        throw new Error('VITE_API_GATEWAY_URL not configured');
    }

    const url = `${API_BASE}${QUOTE_API_PREFIX}/quote-bc/${quoteId}`;

    if (import.meta.env.DEV) {
        console.log('[QuoteAPI] Fetching quote:', url);
    }

    const response = await fetch(url, {
        method: 'GET',
        headers: await getAuthHeaders(),
    });

    if (response.status === 404) {
        if (import.meta.env.DEV) {
            console.log(`[QuoteAPI] Quote not found: ${quoteId}`);
        }
        return null;
    }

    if (response.status === 403) {
        let body = null;
        try {
            body = await response.json();
        } catch (_) {
            /* ignore */
        }
        if (import.meta.env.DEV) {
            console.log(`[QuoteAPI] Not authorized for quote: ${quoteId}`, body);
        }
        Sentry.addBreadcrumb({
            category: 'quote',
            message: `Contact not authorized for quote: ${quoteId}`,
            level: 'warning',
            data: { apiMessage: body?.message || null },
        });
        return { _notAuthorized: true };
    }

    if (!response.ok) {
        let body = '';
        try {
            body = await response.text();
        } catch (_) {
            /* ignore */
        }
        const err = new Error(`HTTP ${response.status}: ${response.statusText}`);
        err.httpStatus = response.status;
        err.responseBody = body.substring(0, 500);
        throw err;
    }

    const quote = await response.json();
    if (!quote) return null;

    // Map ID for Sputnik distribution viewer (Letterbox service class)
    quote.sputnikQuoteId = quote.mapId;

    if (import.meta.env.DEV) {
        console.log('[QuoteAPI] Quote loaded:', {
            quoteId,
            status: quote.quoteStatus,
            isMUF: quote.isMUF,
        });
    }

    return quote;
}

/**
 * Accept a quote by setting its status to ACCEPTED
 *
 * @param {string} quoteId - Quote reference ID
 * @returns {Promise<Object>} API response
 * @throws {Error} On failure
 */
export async function acceptQuote(quoteId) {
    if (!API_BASE) {
        throw new Error('VITE_API_GATEWAY_URL not configured');
    }

    const url = `${API_BASE}${QUOTE_API_PREFIX}/quote-bc/${quoteId}`;

    if (import.meta.env.DEV) {
        console.log('[QuoteAPI] Accepting quote:', quoteId);
    }

    Sentry.addBreadcrumb({
        category: 'quote',
        message: 'Accepting quote',
        data: { quoteId },
    });

    const response = await fetch(url, {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ quoteStatus: 'ACCEPTED' }),
    });

    if (response.status === 403) {
        await handle403(response, quoteId);
    }

    if (!response.ok) {
        throw new Error(`Failed to accept quote: HTTP ${response.status}`);
    }

    if (response.status === 204) {
        if (import.meta.env.DEV) {
            console.log('[QuoteAPI] Quote accepted (no content)');
        }
        return null;
    }

    let data = null;
    try {
        data = await response.json();
    } catch (parseError) {
        Sentry.captureException(parseError, {
            tags: { feature: 'quote-accept', quote_id: quoteId },
            contexts: { response: { status: response.status } },
        });
        if (import.meta.env.DEV) {
            console.warn('[QuoteAPI] Failed to parse accept response body:', parseError);
        }
    }

    if (import.meta.env.DEV) {
        console.log('[QuoteAPI] Quote accepted:', data);
    }

    return data;
}

/**
 * Submit a PO reference for an accepted quote
 *
 * @param {string} quoteId - Quote reference ID
 * @param {string} custRef - Customer PO reference (e.g., "PO123456")
 * @returns {Promise<Object>} API response
 * @throws {Error} On failure
 */
export async function submitPOReference(quoteId, custRef) {
    if (!API_BASE) {
        throw new Error('VITE_API_GATEWAY_URL not configured');
    }

    const url = `${API_BASE}${QUOTE_API_PREFIX}/quote-bc/${quoteId}`;

    if (import.meta.env.DEV) {
        console.log('[QuoteAPI] Submitting PO reference:', { quoteId, custRef });
    }

    Sentry.addBreadcrumb({
        category: 'quote',
        message: 'Submitting PO reference',
        data: { quoteId, custRef },
    });

    const response = await fetch(url, {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ custRef }),
    });

    if (response.status === 403) {
        await handle403(response, quoteId);
    }

    if (!response.ok) {
        throw new Error(`Failed to submit PO reference: HTTP ${response.status}`);
    }

    if (response.status === 204) {
        if (import.meta.env.DEV) {
            console.log('[QuoteAPI] PO reference submitted (no content)');
        }
        return null;
    }

    let data = null;
    try {
        data = await response.json();
    } catch (parseError) {
        Sentry.captureException(parseError, {
            tags: { feature: 'po-reference', quote_id: quoteId },
            contexts: { response: { status: response.status } },
        });
        if (import.meta.env.DEV) {
            console.warn('[QuoteAPI] Failed to parse PO reference response body:', parseError);
        }
    }

    if (import.meta.env.DEV) {
        console.log('[QuoteAPI] PO reference submitted:', data);
    }

    return data;
}

/**
 * Submit the PO requirement flag for an accepted quote
 *
 * @param {string} quoteId - Quote reference ID
 * @param {string} custRefRequired - "Y" or "N"
 * @returns {Promise<Object>} API response
 * @throws {Error} On failure
 */
export async function submitCustRefRequired(quoteId, custRefRequired) {
    if (!API_BASE) {
        throw new Error('VITE_API_GATEWAY_URL not configured');
    }

    const url = `${API_BASE}${QUOTE_API_PREFIX}/quote-bc/${quoteId}`;

    if (import.meta.env.DEV) {
        console.log('[QuoteAPI] Submitting PO requirement:', { quoteId, custRefRequired });
    }

    Sentry.addBreadcrumb({
        category: 'quote',
        message: 'Submitting PO requirement',
        data: { quoteId, custRefRequired },
    });

    const response = await fetch(url, {
        method: 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ custRefRequired }),
    });

    if (response.status === 403) {
        await handle403(response, quoteId);
    }

    if (!response.ok) {
        throw new Error(`Failed to submit PO requirement: HTTP ${response.status}`);
    }

    if (response.status === 204) {
        if (import.meta.env.DEV) {
            console.log('[QuoteAPI] PO requirement submitted (no content)');
        }
        return null;
    }

    let data = null;
    try {
        data = await response.json();
    } catch (parseError) {
        Sentry.captureException(parseError, {
            tags: { feature: 'po-requirement', quote_id: quoteId },
            contexts: { response: { status: response.status } },
        });
        if (import.meta.env.DEV) {
            console.warn('[QuoteAPI] Failed to parse PO requirement response body:', parseError);
        }
    }

    if (import.meta.env.DEV) {
        console.log('[QuoteAPI] PO requirement submitted:', data);
    }

    return data;
}

/**
 * Download an authenticated PDF document via fetch-as-blob.
 *
 * Uses fetch-as-blob because PDF endpoints require a Firebase-Token header,
 * and browser navigation (window.location.href) can't carry custom headers.
 *
 * @param {string} url - Full URL to the document endpoint
 * @param {string} fallbackFilename - Filename if Content-Disposition is missing
 * @throws {Error} On network or HTTP errors
 */
async function downloadDocument(url, fallbackFilename, quoteId) {
    if (import.meta.env.DEV) {
        console.log('[QuoteAPI] Downloading document:', url);
    }

    const headers = await getAuthHeaders();
    headers['Accept'] = '*/*';
    delete headers['Content-Type'];

    const response = await fetch(url, { method: 'GET', headers });

    if (response.status === 403) {
        await handle403(response, quoteId);
    }

    if (response.status === 409) {
        // Retry-After header may not be accessible due to CORS unless the API
        // exposes it via Access-Control-Expose-Headers. Fall back to a default.
        const retryAfter = parseInt(response.headers.get('Retry-After'), 10);
        const DEFAULT_RETRY_MINUTES = 5;
        const minutes = retryAfter > 0 ? Math.ceil(retryAfter / 60) : DEFAULT_RETRY_MINUTES;

        if (import.meta.env.DEV) {
            console.log('[QuoteAPI] Document not ready, retry in', minutes, 'minute(s)');
        }

        throw new DocumentNotReadyError(minutes);
    }

    if (!response.ok) {
        throw new Error(`Failed to download document: HTTP ${response.status}`);
    }

    const blob = await response.blob();

    let filename = fallbackFilename;
    const disposition = response.headers.get('Content-Disposition');
    if (disposition) {
        const match = disposition.match(/filename[^;=\n]*=\s*(?:"([^"]+)"|([^\s;]+))/i);
        if (match) {
            filename = match[1] || match[2];
        }
    }

    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(blobUrl);

    if (import.meta.env.DEV) {
        console.log('[QuoteAPI] Document downloaded:', filename);
    }
}

/**
 * Download the quote PDF document via authenticated fetch.
 *
 * @param {string} quoteId - Quote reference ID
 * @throws {Error} On network or HTTP errors
 */
export async function downloadQuoteDocument(quoteId) {
    if (!API_BASE) {
        throw new Error('VITE_API_GATEWAY_URL not configured');
    }

    const url = `${API_BASE}${QUOTE_API_PREFIX}/quote-bc-doc/${quoteId}`;
    await downloadDocument(url, `quote-${quoteId}.pdf`, quoteId);
}

/**
 * Download the invoice PDF document via authenticated fetch.
 *
 * @param {string} quoteId - Quote reference ID
 * @throws {Error} On network or HTTP errors
 */
export async function downloadInvoiceDocument(quoteId) {
    if (!API_BASE) {
        throw new Error('VITE_API_GATEWAY_URL not configured');
    }

    const url = `${API_BASE}${QUOTE_API_PREFIX}/quote-invoice-doc/${quoteId}`;
    await downloadDocument(url, `invoice-${quoteId}.pdf`, quoteId);
}
